
import React, { useState, useEffect, useCallback } from "react";
import { usePost } from "seed/api";
import { useDetail } from "seed/gql";
import { detectBrowserLanguage } from "components/util/l.lib";
import l from "components/util/l";
import { INDUSTRIES } from "components/tmisc/get_started/GetStarted.lib.catalogs";
import View from "./GetStarted.view";


const TOTAL_STEPS = 2;
// What the progress counts: the form's steps plus the walkthrough the portal opens after
// them. Counting only the form read "2 de 2" right before a 5-to-15-minute conversation.
const TOTAL_PHASES = TOTAL_STEPS + 1;

function clearStorageExcept(storage, keysToKeep = []) {
  const allowedKeys = new Set(keysToKeep);
  const keys = Object.keys(storage);
  keys.forEach((key) => {
    if (!allowedKeys.has(key))
      storage.removeItem(key);
  });
}

function GetStarted() {

  const [formData, setFormData] = useState(() => {
    const params = new URLSearchParams(window.location.search);

    const websiteParam = params.get("website") || "";
    const industryParam = params.get("industry") || "";
    const areaParam = params.get("area") || "";

    // No name and no email: this form asks what they do, and the assistant asks who they
    // are at the end — and only when it finds it does not already know. Asking twice is
    // what made the shortest path through the funnel start with a wall of identity fields.
    return {
      step: 1,
      area: areaParam,
      error: "",
      industry: industryParam,
      position: "",
      useManual: true, //Temp hide 251118 !websiteParam && hasManualData,
      company_url: websiteParam,
      services: "",
    };
  });

  const userId = sessionStorage.getItem("id");

  const percent = Math.round((formData.step / TOTAL_PHASES) * 100);

  // Any edit clears the error: it was about the values as they were, and the visitor is
  // changing them.
  const updateFormData = useCallback((updates) => {
    setFormData((prev) => ({ ...prev, error: "", ...updates }));
  }, []);

  const getContexts = () => {

    const {
      company_url,
      area,
      industry,
      position,
      services,
    } = formData;

    const contexts = [];

    if (company_url) {
      contexts.push({
        category: "company_url",
        values: [company_url],
      });
    }

    if (industry) {
      contexts.push({
        category: "industry",
        values: [industry],
      });
    }

    if (services.length) {
      contexts.push({
        category: "services",
        values: [services],
      });
    }

    if (area) {
      contexts.push({
        category: "area",
        values: [area],
      });
    }

    if (position) {
      contexts.push({
        category: "position",
        values: [position],
      });
    }

    // Identity used to travel here too, under full_name and email, and the API read it
    // out to name the account. It no longer does: the visitor gets an anonymous account
    // and the assistant names it with `sb.users.set_identity` once it has an address.
    return contexts;

  }

  const [callGetCompanyInfo, reqGetCompanyInfo] = usePost("/users/get_company_info", {
    onCompleted: (data) => {
      const { services, industry } = data;
      setFormData((prev) => ({
        ...prev,
        industry: industry || prev.industry,
        services: services || prev.services,
        step: 2,
      }));
    },
    onError: () => {
      updateFormData({
        error: ""
      });
    }
  });

  // Their project, which opens on the portal — and the portal is what frames the
  // Onboarding assistant. Navigating rather than swapping what this screen renders is the
  // point: the form is done, so a reload has to land there and not ask for the same data
  // again.
  const openProject = ({ flow }) => {
    window.location.href = `/app/?flow_id=${flow}`;
  };

  const [callFinishOnboarding, reqFinishOnboarding] = usePost(
    "/users/finish_onboarding",
    {
      onCompleted: openProject,
    }
  );

  const [callFinishAnonymousOnboarding, reqFinishAnonymousOnboarding] = usePost(
    "/users/finish_anonymous_onboarding",
    {
      onCompleted: (data) => {
        const { user, token } = data;
        localStorage.setItem("id", user);
        localStorage.setItem("token", token);
        sessionStorage.setItem("id", user);
        sessionStorage.setItem("token", token);
        openProject(data);
      },
      // The 409 was "that email already has an account", and this form no longer sends one:
      // kept in case an older entry point still does.
      onError: (error) =>
        updateFormData({
          error: error?.status == 409
            ? l("Ese correo ya tiene una cuenta, inicia sesión para continuar%GS921")
            : l("Ha ocurrido un error%GA019"),
        }),
    }
  );

  const reqUser = useDetail(`{
    user {
      firstName
      lastName
      email
      isAnonymous
      accountSettings
    }
  }`, userId);

  const onClickNextStep = () => {
    
    const { step, useManual, company_url, industry } = formData;
    if (step === 1 && !useManual) {
      callGetCompanyInfo({ company_url: company_url });
      return;
    }

    // The industry is a catalog, not free text: it is what the base is counted by, so an
    // empty field or a hand-typed word ("tecnología") does not get past this step.
    if (step === 1 && !INDUSTRIES.includes(industry)) {
      updateFormData({ error: l("Elige tu industria principal de la lista%GS924") });
      return;
    }

    updateFormData({
      step: Math.min(TOTAL_STEPS, step + 1),
    });

  };

  const onClickBackStep = () =>
    updateFormData({ step: Math.max(1, formData.step - 1) });

  const onClickFinish = () => {

    const userContexts = getContexts();

    const payload = {
      language: detectBrowserLanguage(),
      user_contexts: userContexts,
    };

    const request = {
      account_settings: payload,
      flow_name: l("Nueva solución%GA104"),
    };

    if (userId)
      callFinishOnboarding(request);
    else
      callFinishAnonymousOnboarding(request);

  };

  const isLoading =
    reqFinishOnboarding.loading ||
    reqFinishAnonymousOnboarding.loading ||
    reqGetCompanyInfo.loading;

  useEffect(() => {
    if (reqUser.loading) return;
    if (userId)
      return;
    clearStorageExcept(sessionStorage, ["language"]);
    clearStorageExcept(localStorage, ["language"]);
  }, [reqUser.loading, userId]);

  return (
    <View
      formData={formData}
      totalSteps={TOTAL_STEPS}
      totalPhases={TOTAL_PHASES}
      percent={percent}
      isLoading={isLoading}
      updateFormData={updateFormData}
      onClickBackStep={onClickBackStep}
      onClickNextStep={onClickNextStep}
      onClickFinish={onClickFinish}
    />
  );
}

export default GetStarted;
